import React, { useState, useEffect, useRef } from 'react';
import { NotebookPage } from '../../pages/NotebookPage';
import RichTextEditor from './RichTextEditor';

interface PageEditorProps {
  page: NotebookPage | null;
  onSave: (pageData: Partial<NotebookPage>) => void;
}

const PageEditor: React.FC<PageEditorProps> = ({ page, onSave }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  // Update local state when page changes
  useEffect(() => {
    if (page) {
      setTitle(page.title);
      setContent(page.content);
      setIsDirty(false);
    } else {
      setTitle('');
      setContent('');
      setIsDirty(false);
    }
  }, [page]);

  // Auto-save functionality
  useEffect(() => {
    if (isDirty && page) {
      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set new timeout for auto-save
      saveTimeoutRef.current = setTimeout(() => {
        handleSave();
      }, 2000); // Auto-save after 2 seconds of inactivity
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [isDirty, title, content, page]);

  const handleSave = async () => {
    if (!page || !isDirty) return;

    try {
      setIsSaving(true);
      await onSave({
        id: page.id,
        title: title.trim() || 'Untitled Note',
        content,
      });
      setIsDirty(false);
    } catch (error) {
      console.error('Failed to save page:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    setIsDirty(true);
  };

  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    setIsDirty(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Save on Ctrl+S
    if (e.ctrlKey && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  };

  if (!page) {
    return (
      <div className="flex flex-col h-full bg-gray-50">
        <div className="flex-1 flex items-center justify-center text-gray-500">
          <div className="text-center">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-lg font-medium mb-2">Welcome to NoteMann</h3>
            <p className="text-sm max-w-md">
              Create folders to organize your thoughts, then add pages to capture your insights. 
              Use the rich text editor to format your content beautifully.
            </p>
            <div className="mt-4 text-xs text-gray-400">
              <p>✨ Rich text formatting</p>
              <p>💾 Auto-save every 2 seconds</p>
              <p>⌨️ Keyboard shortcuts (Ctrl+B, Ctrl+I, etc.)</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2 flex-1">
            <input
              type="text"
              value={title}
              onChange={handleTitleChange}
              onKeyDown={handleKeyDown}
              placeholder="Untitled Note"
              className="text-xl font-semibold text-gray-900 bg-transparent border-none outline-none focus:ring-0 p-0 flex-1"
            />
            {isDirty && (
              <span className="text-xs text-orange-500 flex items-center space-x-1">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span>Unsaved</span>
              </span>
            )}
            {isSaving && (
              <span className="text-xs text-blue-500 flex items-center space-x-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span>Saving...</span>
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              Save
            </button>
          </div>
        </div>

        {/* Metadata */}
        <div className="flex items-center space-x-4 text-xs text-gray-500">
          <span>Created {new Date(page.createdAt).toLocaleDateString()}</span>
          <span>•</span>
          <span>Updated {new Date(page.updatedAt).toLocaleDateString()}</span>
          {page.linkedClients.length > 0 && (
            <>
              <span>•</span>
              <span>{page.linkedClients.length} clients linked</span>
            </>
          )}
          {page.linkedTranscripts.length > 0 && (
            <>
              <span>•</span>
              <span>{page.linkedTranscripts.length} transcripts linked</span>
            </>
          )}
        </div>
      </div>

      {/* Rich Text Editor */}
      <div className="flex-1 overflow-hidden">
        <RichTextEditor
          content={content}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          placeholder="Start writing your note... Use the toolbar above for formatting!"
        />
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-4">
            <span>Rich text editor</span>
            <span>•</span>
            <span>Press Ctrl+S to save</span>
            <span>•</span>
            <span>Auto-save enabled</span>
          </div>
          <div className="flex items-center space-x-2">
            <button className="text-gray-400 hover:text-gray-600 transition-colors" title="Link Clients">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </button>
            <button className="text-gray-400 hover:text-gray-600 transition-colors" title="Link Transcripts">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
            <button className="text-gray-400 hover:text-gray-600 transition-colors" title="Add Tags">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageEditor; 